/**
 * The KPI formula library (Blueprint Appendix A). Every KPI exists here once, as
 * a pure function over exact integer cents. Screens, alerts, reports and AI
 * commentary all use these results; none of them recalculates a KPI.
 *
 * When a KPI can't be meaningfully computed (no revenue, negative equity, cash
 * not going down...), it returns `exact: null` with a plain-English reason,
 * never a misleading number.
 */
import type { Cents } from "../money";
import { formatCents } from "../money";
import { Exact } from "./exact";

/** What DSO divides receivables by. Set per client (CFO › Settings); total revenue unless staff choose otherwise. */
export type DsoBasis = "total_revenue" | "invoiced_sales";
export const DSO_BASES: DsoBasis[] = ["total_revenue", "invoiced_sales"];
/** Plain words for each basis: the setting's label, and the sentence the scorecard shows. */
export const DSO_BASIS_TEXT: Record<DsoBasis, { label: string; sentence: string }> = {
  total_revenue: { label: "Total revenue", sentence: "Days sales outstanding is based on total revenue." },
  invoiced_sales: { label: "Invoiced (credit) sales only", sentence: "Days sales outstanding is based on invoiced (credit) sales only; cash sales are left out." },
};

/** Per-client choices that change how a KPI is calculated. Required wherever KPIs are computed, so none can be forgotten. */
export type KpiSettings = { dsoBasis: DsoBasis };

/** Flows for one period (one month, a quarter, trailing twelve months...), from the P&L. */
export type PeriodFigures = {
  /** Calendar days the period covers (DSO, DPO and DIO use it). */
  days: number;
  /** Total Income. */
  revenue: Cents;
  /** The income accounts staff mapped as invoiced (credit) sales; null when not mapped. DSO uses it when the client's basis is invoiced sales. */
  invoicedSales: Cents | null;
  /** Total Cost of Goods Sold (0 when the business has none). */
  cogs: Cents;
  /** Net Operating Income. */
  operatingIncome: Cents;
  /** QuickBooks' Total Expenses, stored with snapshots from October 2026 (older ones derive it: revenue − COGS − operating income). */
  operatingExpenses?: Cents;
  netIncome: Cents;
  /** From the accounts staff mapped to each category; null when not mapped yet. */
  interestExpense: Cents | null;
  /** Interest earned (mapped income accounts); null when not mapped, and EBITDA then counts it as $0 with a note. */
  interestIncome: Cents | null;
  taxes: Cents | null;
  depreciationAmortization: Cents | null;
  payrollCost: Cents | null;
  /** The part of payroll booked in cost of goods sold or operating expenses (DPO leaves it out of purchases); null when not mapped. */
  payrollInOperatingCosts: Cents | null;
  /** The part of depreciation & amortization booked in cost of goods sold or operating expenses (DPO leaves it out of purchases); null when not mapped. */
  depreciationInOperatingCosts: Cents | null;
  fixedCosts: Cents | null;
  /** Principal plus interest due in the period, entered by staff. */
  debtService: Cents | null;
};

/** Balances at the end of the period, from the balance sheet. */
export type BalanceFigures = {
  cash: Cents;
  ar: Cents;
  ap: Cents;
  inventory: Cents;
  marketableSecurities: Cents;
  currentAssets: Cents;
  currentLiabilities: Cents;
  totalLiabilities: Cents;
  equity: Cents;
};

/**
 * Every count of employees known for the period. Revenue per employee uses the first one known, in
 * this order: the staff override; employees actually paid in the period (payroll data); active
 * employees in QuickBooks. Null = not known.
 */
export type HeadcountSources = { override: number | null; paidInPeriod: number | null; activeInQuickBooks: number | null };
export type HeadcountSource = "override" | "payroll" | "quickbooks";
export type Headcount = { count: number; source: HeadcountSource };

const HEADCOUNT_WORDS: Record<HeadcountSource, (n: number) => string> = {
  override: (n) => `Headcount entered by staff: ${n}`,
  payroll: (n) => `${n} employee${n === 1 ? "" : "s"} paid in the period (payroll)`,
  quickbooks: (n) => `${n} active employee${n === 1 ? "" : "s"} in QuickBooks`,
};

/** The headcount revenue per employee divides by: the override when set, else employees paid (payroll), else active QuickBooks employees. */
export function resolveHeadcount(h: HeadcountSources): Headcount | null {
  if (h.override !== null) return { count: h.override, source: "override" };
  if (h.paidInPeriod !== null) return { count: h.paidInPeriod, source: "payroll" };
  if (h.activeInQuickBooks !== null) return { count: h.activeInQuickBooks, source: "quickbooks" };
  return null;
}

export type KpiInputs = {
  period: PeriodFigures;
  balance: BalanceFigures;
  /** Cash at the end of each of the last four months, oldest first: [m−3, m−2, m−1, m]. Burn needs all four. */
  cashMonthEnds: Cents[] | null;
  headcount: HeadcountSources;
  settings: KpiSettings;
};

export type KpiKind = "money" | "percent" | "ratio" | "days" | "months";

export type KpiKey =
  | "net_burn"
  | "runway"
  | "current_ratio"
  | "quick_ratio"
  | "working_capital"
  | "gross_margin"
  | "operating_margin"
  | "net_margin"
  | "ebitda"
  | "break_even_revenue"
  | "dso"
  | "dpo"
  | "dio"
  | "cash_conversion_cycle"
  | "debt_to_equity"
  | "dscr"
  | "revenue_per_employee"
  | "payroll_pct_revenue";

export type KpiResult = {
  key: KpiKey;
  label: string;
  kind: KpiKind;
  /** Exact value: a fraction for percent (0.423 = 42.3%), cents for money. Null when not meaningful. */
  exact: Exact | null;
  /** Rounded display, e.g. "42.3%", "1.85", "$12,345.67", "38.5 days". "—" when not meaningful. */
  display: string;
  /** Why the value is missing, or a caveat. */
  note: string | null;
};

export type KpiDefinition = { key: KpiKey; label: string; kind: KpiKind; formula: string; higherIsBetter: boolean | null };

/** Definitions, as shown to staff next to each KPI (Blueprint Appendix A). */
export const KPI_DEFINITIONS: Record<KpiKey, KpiDefinition> = {
  net_burn: { key: "net_burn", label: "Net burn (monthly)", kind: "money", formula: "(Cash at the start of the 3-month window − cash at its end) ÷ 3", higherIsBetter: false },
  runway: { key: "runway", label: "Runway at current burn", kind: "months", formula: "Cash ÷ average monthly net burn (last three months)", higherIsBetter: true },
  current_ratio: { key: "current_ratio", label: "Current ratio", kind: "ratio", formula: "Current assets ÷ current liabilities", higherIsBetter: true },
  quick_ratio: { key: "quick_ratio", label: "Quick ratio", kind: "ratio", formula: "(Cash + accounts receivable + marketable securities) ÷ current liabilities", higherIsBetter: true },
  working_capital: { key: "working_capital", label: "Working capital", kind: "money", formula: "Current assets − current liabilities", higherIsBetter: true },
  gross_margin: { key: "gross_margin", label: "Gross margin", kind: "percent", formula: "(Revenue − cost of goods sold) ÷ revenue", higherIsBetter: true },
  operating_margin: { key: "operating_margin", label: "Operating margin", kind: "percent", formula: "Operating income ÷ revenue", higherIsBetter: true },
  net_margin: { key: "net_margin", label: "Net margin", kind: "percent", formula: "Net income ÷ revenue", higherIsBetter: true },
  ebitda: { key: "ebitda", label: "EBITDA", kind: "money", formula: "Net income + interest expense − interest income + taxes + depreciation & amortization", higherIsBetter: true },
  break_even_revenue: { key: "break_even_revenue", label: "Break-even revenue", kind: "money", formula: "Fixed costs ÷ gross margin %", higherIsBetter: false },
  dso: { key: "dso", label: "Days sales outstanding", kind: "days", formula: "(Accounts receivable ÷ sales) × days in period, where sales are total revenue, or invoiced (credit) sales only when the client is set to that", higherIsBetter: false },
  dpo: { key: "dpo", label: "Days payables outstanding", kind: "days", formula: "(Accounts payable ÷ purchases) × days in period, where purchases = cost of goods sold + operating expenses − payroll − depreciation & amortization", higherIsBetter: null },
  dio: { key: "dio", label: "Days inventory outstanding", kind: "days", formula: "(Inventory ÷ cost of goods sold) × days in period", higherIsBetter: false },
  cash_conversion_cycle: { key: "cash_conversion_cycle", label: "Cash conversion cycle", kind: "days", formula: "DSO + DIO − DPO", higherIsBetter: false },
  debt_to_equity: { key: "debt_to_equity", label: "Debt-to-equity", kind: "ratio", formula: "Total liabilities ÷ equity", higherIsBetter: false },
  dscr: { key: "dscr", label: "Debt service coverage", kind: "ratio", formula: "EBITDA ÷ total debt service", higherIsBetter: true },
  revenue_per_employee: { key: "revenue_per_employee", label: "Revenue per employee (month)", kind: "money", formula: "Revenue for the month ÷ headcount (the staff override when set, else employees paid in the month from payroll, else active employees in QuickBooks)", higherIsBetter: true },
  payroll_pct_revenue: { key: "payroll_pct_revenue", label: "Payroll % of revenue", kind: "percent", formula: "Total payroll cost ÷ revenue", higherIsBetter: false },
};

export const KPI_ORDER: KpiKey[] = Object.keys(KPI_DEFINITIONS) as KpiKey[];

const PLACES: Record<KpiKind, number> = { money: 0, percent: 1, ratio: 2, days: 1, months: 1 };

/** The one display rule per kind (see exact.ts for rounding). */
export function displayKpi(kind: KpiKind, v: Exact | null): string {
  if (!v) return "—";
  switch (kind) {
    case "money":
      return formatCents(v.toCents());
    case "percent":
      return `${v.mul(Exact.int(100)).toFixed(PLACES.percent)}%`;
    case "ratio":
      return v.toFixed(PLACES.ratio);
    case "days":
      return `${v.toFixed(PLACES.days)} days`;
    case "months":
      return `${v.toFixed(PLACES.months)} months`;
  }
}

const c = (v: Cents) => Exact.int(v);

function result(key: KpiKey, exact: Exact | null, note: string | null = null): KpiResult {
  const def = KPI_DEFINITIONS[key];
  return { key, label: def.label, kind: def.kind, exact, display: displayKpi(def.kind, exact), note };
}
const none = (key: KpiKey, why: string) => result(key, null, why);

/** Average monthly net burn over the last three months; positive means cash went down. */
function netBurn(i: KpiInputs): Exact | null {
  const m = i.cashMonthEnds;
  if (!m || m.length !== 4) return null;
  return c(m[0]!).sub(c(m[3]!)).div(Exact.int(3));
}

/** Operating expenses: QuickBooks' Total Expenses, or (older snapshots) revenue − cost of goods sold − operating income. */
export function operatingExpensesOf(p: PeriodFigures): Cents {
  return p.operatingExpenses ?? p.revenue - p.cogs - p.operatingIncome;
}

/** Purchases for DPO: cost of goods sold + operating expenses, less the payroll and depreciation & amortization in them. Null until both are mapped. */
export function dpoPurchases(p: PeriodFigures): Cents | null {
  if (p.payrollInOperatingCosts === null || p.depreciationInOperatingCosts === null) return null;
  return p.cogs + operatingExpensesOf(p) - p.payrollInOperatingCosts - p.depreciationInOperatingCosts;
}

/** The sales DSO divides by, for the client's basis: total revenue, or invoiced (credit) sales (null until mapped). */
export function dsoSales(i: KpiInputs): Cents | null {
  return i.settings.dsoBasis === "invoiced_sales" ? i.period.invoicedSales : i.period.revenue;
}

function ebitda(p: PeriodFigures): Exact | null {
  if (p.interestExpense === null || p.taxes === null || p.depreciationAmortization === null) return null;
  // Interest income not mapped counts as $0 (the EBITDA result says so).
  return c(p.netIncome).add(c(p.interestExpense)).sub(c(p.interestIncome ?? 0)).add(c(p.taxes)).add(c(p.depreciationAmortization));
}

/** Days metrics: (balance ÷ flow) × days; null when the flow is zero or negative. The one place DSO, DPO and DIO divide. */
export function daysOutstanding(balance: Cents, flow: Cents, days: number): Exact | null {
  if (flow <= 0) return null;
  return c(balance).div(c(flow)).mul(Exact.int(days));
}
const daysOf = daysOutstanding;

export function computeKpis(i: KpiInputs): Record<KpiKey, KpiResult> {
  const p = i.period;
  const b = i.balance;
  if (!Number.isInteger(p.days) || p.days <= 0) throw new RangeError("Period days must be a positive whole number");
  if (!DSO_BASES.includes(i.settings?.dsoBasis)) throw new RangeError("KPI settings must say which sales DSO uses");
  const rev = c(p.revenue);
  const noRevenue = p.revenue <= 0;
  const out = {} as Record<KpiKey, KpiResult>;

  const burn = netBurn(i);
  out.net_burn = burn ? result("net_burn", burn, burn.sign() <= 0 ? "Cash grew over the last three months" : null) : none("net_burn", "Needs four month-end cash balances");
  out.runway = !burn
    ? none("runway", "Needs four month-end cash balances")
    : burn.sign() <= 0
      ? none("runway", burn.sign() === 0 ? "Cash didn't change over the last three months, so there's no runway limit" : "Cash isn't going down, so there's no runway limit")
      : b.cash <= 0
        ? result("runway", Exact.int(0), "No cash left")
        : result("runway", c(b.cash).div(burn));

  const noCl = b.currentLiabilities === 0 ? "No current liabilities" : "Current liabilities are negative (a credit balance); check the balance sheet";
  out.current_ratio = b.currentLiabilities > 0 ? result("current_ratio", c(b.currentAssets).div(c(b.currentLiabilities))) : none("current_ratio", noCl);
  out.quick_ratio = b.currentLiabilities > 0 ? result("quick_ratio", c(b.cash).add(c(b.ar)).add(c(b.marketableSecurities)).div(c(b.currentLiabilities))) : none("quick_ratio", noCl);
  out.working_capital = result("working_capital", c(b.currentAssets).sub(c(b.currentLiabilities)));

  const gp = rev.sub(c(p.cogs));
  out.gross_margin = noRevenue ? none("gross_margin", "No revenue in the period") : result("gross_margin", gp.div(rev));
  out.operating_margin = noRevenue ? none("operating_margin", "No revenue in the period") : result("operating_margin", c(p.operatingIncome).div(rev));
  out.net_margin = noRevenue ? none("net_margin", "No revenue in the period") : result("net_margin", c(p.netIncome).div(rev));

  const e = ebitda(p);
  out.ebitda = e
    ? result("ebitda", e, p.interestIncome === null ? "Interest income isn't mapped, so it counts as $0" : null)
    : none("ebitda", "Map the interest expense, tax and depreciation accounts first");

  out.break_even_revenue =
    p.fixedCosts === null
      ? none("break_even_revenue", "Map the fixed-cost accounts first")
      : noRevenue
        ? none("break_even_revenue", "No revenue in the period")
        : gp.sign() <= 0
          ? none("break_even_revenue", "Gross margin is zero or negative, so sales can't cover fixed costs")
          : // Fixed ÷ (GP ÷ revenue) = fixed × revenue ÷ GP
            result("break_even_revenue", c(p.fixedCosts).mul(rev).div(gp));

  const invoiced = i.settings.dsoBasis === "invoiced_sales";
  const sales = dsoSales(i);
  const dso = sales === null ? null : daysOf(b.ar, sales, p.days);
  const purchases = dpoPurchases(p);
  const dpo = purchases === null ? null : daysOf(b.ap, purchases, p.days);
  const dio = daysOf(b.inventory, p.cogs, p.days);
  out.dso = dso
    ? result("dso", dso, invoiced ? "On invoiced (credit) sales only" : null)
    : none("dso", sales === null ? "Map the invoiced (credit) sales accounts first, or refresh this month from QuickBooks" : invoiced ? "No invoiced (credit) sales in the period" : "No revenue in the period");
  out.dpo = dpo
    ? result("dpo", dpo)
    : none("dpo", purchases === null ? "Map the payroll and depreciation accounts first, or refresh this month from QuickBooks" : "No purchases in the period (cost of goods sold and operating expenses, less payroll and depreciation)");
  out.dio = dio ? result("dio", dio) : none("dio", "No cost of goods sold in the period");
  // By convention the cycle needs all three parts; a service business with no cost of goods gets "—" (DSO still shows).
  out.cash_conversion_cycle = dso && dpo && dio ? result("cash_conversion_cycle", dso.add(dio).sub(dpo)) : none("cash_conversion_cycle", "Needs DSO, DIO and DPO (see DSO for a service business)");

  out.debt_to_equity = b.equity > 0 ? result("debt_to_equity", c(b.totalLiabilities).div(c(b.equity))) : none("debt_to_equity", "Equity is zero or negative");
  out.dscr =
    p.debtService === null || p.debtService <= 0
      ? none("dscr", p.debtService === null ? "Enter the period's debt payments first" : "No debt payments in the period")
      : !e
        ? none("dscr", "Map the interest expense, tax and depreciation accounts first")
        : result("dscr", e.div(c(p.debtService)));

  const hc = resolveHeadcount(i.headcount);
  out.revenue_per_employee = !hc
    ? none("revenue_per_employee", "Headcount unknown: no payroll data, no QuickBooks employees and no override")
    : hc.count <= 0
      ? none("revenue_per_employee", `${HEADCOUNT_WORDS[hc.source](hc.count)}, so there is no revenue per employee`)
      : result("revenue_per_employee", rev.div(Exact.int(hc.count)), HEADCOUNT_WORDS[hc.source](hc.count));
  out.payroll_pct_revenue =
    p.payrollCost === null
      ? none("payroll_pct_revenue", "Map the payroll accounts first")
      : noRevenue
        ? none("payroll_pct_revenue", "No revenue in the period")
        : result("payroll_pct_revenue", c(p.payrollCost).div(rev));

  return out;
}

// --- Stored snapshots ---------------------------------------------------------------------------

const centsOrNull = (v: unknown): Cents | null => (typeof v === "number" && Number.isSafeInteger(v) ? v : null);
const countOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

/**
 * KPI inputs as stored with a monthly snapshot, in any version, with the client's current settings
 * applied. Snapshots are recomputed on read with this, so every screen shows the current definitions:
 * - before October 2026 they kept one "interest" figure (the mapped interest expense) and one
 *   headcount (read here as QuickBooks' count); interest income, invoiced sales and the payroll and
 *   depreciation inside operating costs weren't recorded, so they read as unknown (null) until the
 *   month is refreshed from QuickBooks. DPO, and DSO on invoiced sales, then show "—" with that reason.
 */
export function readStoredInputs(raw: unknown, settings: KpiSettings): KpiInputs {
  const r = (raw ?? {}) as Record<string, unknown>;
  const p = (r.period ?? {}) as Record<string, unknown>;
  const bal = (r.balance ?? {}) as Record<string, unknown>;
  const num = (v: unknown, what: string): number => {
    if (typeof v !== "number" || !Number.isSafeInteger(v)) throw new RangeError(`Stored KPI inputs are missing ${what}`);
    return v;
  };
  const h = r.headcount;
  const headcount: HeadcountSources =
    h && typeof h === "object"
      ? { override: countOrNull((h as Record<string, unknown>).override), paidInPeriod: countOrNull((h as Record<string, unknown>).paidInPeriod), activeInQuickBooks: countOrNull((h as Record<string, unknown>).activeInQuickBooks) }
      : { override: null, paidInPeriod: null, activeInQuickBooks: countOrNull(h) };
  const months = Array.isArray(r.cashMonthEnds) && r.cashMonthEnds.length === 4 && r.cashMonthEnds.every((x) => centsOrNull(x) !== null) ? (r.cashMonthEnds as Cents[]) : null;
  return {
    period: {
      days: num(p.days, "days"),
      revenue: num(p.revenue, "revenue"),
      invoicedSales: centsOrNull(p.invoicedSales),
      cogs: num(p.cogs, "cost of goods sold"),
      operatingIncome: num(p.operatingIncome, "operating income"),
      ...(centsOrNull(p.operatingExpenses) !== null ? { operatingExpenses: p.operatingExpenses as Cents } : {}),
      netIncome: num(p.netIncome, "net income"),
      interestExpense: "interestExpense" in p ? centsOrNull(p.interestExpense) : centsOrNull(p.interest),
      interestIncome: centsOrNull(p.interestIncome),
      taxes: centsOrNull(p.taxes),
      depreciationAmortization: centsOrNull(p.depreciationAmortization),
      payrollCost: centsOrNull(p.payrollCost),
      payrollInOperatingCosts: centsOrNull(p.payrollInOperatingCosts),
      depreciationInOperatingCosts: centsOrNull(p.depreciationInOperatingCosts),
      fixedCosts: centsOrNull(p.fixedCosts),
      debtService: centsOrNull(p.debtService),
    },
    balance: {
      cash: num(bal.cash, "cash"),
      ar: num(bal.ar, "receivables"),
      ap: num(bal.ap, "payables"),
      inventory: num(bal.inventory, "inventory"),
      marketableSecurities: num(bal.marketableSecurities, "marketable securities"),
      currentAssets: num(bal.currentAssets, "current assets"),
      currentLiabilities: num(bal.currentLiabilities, "current liabilities"),
      totalLiabilities: num(bal.totalLiabilities, "total liabilities"),
      equity: num(bal.equity, "equity"),
    },
    cashMonthEnds: months,
    headcount,
    settings,
  };
}

/** A runway figure as people read it: "Not burning cash" when cash isn't falling, rather than a dash. */
export function runwayReading(runway: { display: string; exact: Exact | string | null; note: string | null }, netBurn: { exact: Exact | string | null }): { display: string; note: string | null; notBurning: boolean } {
  const burn = typeof netBurn.exact === "string" ? Exact.parse(netBurn.exact) : netBurn.exact;
  const notBurning = !runway.exact && burn !== null && burn.sign() <= 0;
  return notBurning ? { display: "Not burning cash", note: "Cash grew or held steady over the last three months", notBurning } : { display: runway.display, note: runway.note, notBurning };
}
